import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../services/api';
import { 
    Users, 
    BookOpen, 
    Radio, 
    RefreshCcw, 
    Trash2, 
    Shield, 
    BarChart3, 
    Search,
    ChevronRight,
    Power,
    Play,
    Pause,
    MoreVertical,
    Trophy,
    Plus,
    Award
} from 'lucide-react';
import { VERIFIED_MEMBERS } from './public/VerifyCertificate';
import { getAllPoints, addPoints, savePoints, resetAllPointsToForm, getRankedMembers, getRoleForRank } from '../services/referralService';

const AdminDashboard = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('users');
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [courses, setCourses] = useState([]);
    const [liveClasses, setLiveClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [actionMessage, setActionMessage] = useState('');
    const [isPolling, setIsPolling] = useState(true);
    const [isOfflineMode, setIsOfflineMode] = useState(false);
    const [pointsState, setPointsState] = useState(getAllPoints());

    const handleAddPoints = (refId, memberName, amount) => {
        const updated = addPoints(refId, amount);
        setPointsState({ ...updated });
        showToast(`+${amount} Points credited to ${memberName} (₹${amount * 10} fee recorded)!`);
    };

    const fetchData = useCallback(async (showLoading = true) => {
        try {
            if (showLoading) setLoading(true);
            const [statsRes, usersRes, coursesRes, liveRes] = await Promise.all([
                adminAPI.getStats(),
                adminAPI.getUsers(),
                adminAPI.getCourses(),
                adminAPI.getLiveClasses()
            ]);
            setStats(statsRes.data);
            setUsers(usersRes.data);
            setCourses(coursesRes.data);
            setLiveClasses(liveRes.data);
            setError('');
        } catch (err) {
            console.error('Admin Data Fetch Error:', err);
            if (err.response?.status === 403) {
                setError('Access denied. Admin role required.');
            } else {
                setIsOfflineMode(true);
                setError('');
            }
        } finally {
            if (showLoading) setLoading(false);
        }
    }, []);

    // Initial load and polling
    useEffect(() => {
        fetchData(true);
        let interval;
        if (isPolling) {
            interval = setInterval(() => {
                fetchData(false); // Background refresh
            }, 30000); // 30 seconds
        }
        return () => clearInterval(interval);
    }, [fetchData, isPolling]);

    const handleRoleChange = async (userId, newRole) => {
        try {
            await adminAPI.updateUserRole(userId, newRole);
            showToast(`Role updated to "${newRole}"`);
            fetchData(false);
        } catch (err) {
            showToast(err.response?.data?.detail || 'Update failed', true);
        }
    };

    const handleToggleActive = async (userId) => {
        try {
            await adminAPI.toggleUserActive(userId);
            showToast('User status toggled');
            fetchData(false);
        } catch (err) {
            showToast('Toggle failed', true);
        }
    };

    const handleDeleteUser = async (userId, email) => {
        if (!window.confirm(`Delete ${email}? Irreversible!`)) return;
        try {
            await adminAPI.deleteUser(userId);
            showToast('User deleted');
            fetchData(false);
        } catch (err) {
            showToast('Delete failed', true);
        }
    };

    const showToast = (msg, isError = false) => {
        setActionMessage(msg);
        setTimeout(() => setActionMessage(''), 3000);
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
    };

    const filteredUsers = users.filter(user => {
        const matchesSearch = user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (user.full_name || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesRole = roleFilter === 'all' || user.role === roleFilter;
        return matchesSearch && matchesRole;
    });

    if (loading && !stats) {
        return (
            <div style={styles.loadingContainer}>
                <div style={styles.spinner}></div>
                <p style={styles.loadingText}>Initializing ARIVUNEST Control Center...</p>
            </div>
        );
    }

    if (error && error.includes('Access denied')) {
        return (
            <div style={styles.errorContainer}>
                <Shield size={64} color="#dc2626" />
                <h2 style={styles.errorTitle}>{error}</h2>
                <button style={styles.primaryButton} onClick={() => navigate('/')}>Return to Safety</button>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            {/* Sidebar Navigation */}
            <aside style={styles.sidebar}>
                <div style={styles.logoSlot}>
                    <div style={styles.logoIcon}>AN</div>
                    <span style={styles.logoText}>Control Panel</span>
                </div>
                
                <nav style={styles.nav}>
                    <button 
                        style={activeTab === 'users' ? styles.navItemActive : styles.navItem} 
                        onClick={() => setActiveTab('users')}
                    >
                        <Users size={20} />
                        <span>Users</span>
                    </button>
                    <button 
                        style={activeTab === 'courses' ? styles.navItemActive : styles.navItem} 
                        onClick={() => setActiveTab('courses')}
                    >
                        <BookOpen size={20} />
                        <span>Courses Catalog</span>
                    </button>
                    <button 
                        style={activeTab === 'live' ? styles.navItemActive : styles.navItem} 
                        onClick={() => setActiveTab('live')}
                    >
                        <Radio size={20} />
                        <span>Live Classes</span>
                    </button>
                    <button 
                        style={activeTab === 'referrals' ? styles.navItemActive : styles.navItem} 
                        onClick={() => setActiveTab('referrals')}
                    >
                        <Trophy size={20} />
                        <span>Referral Points & Election</span>
                    </button>
                </nav>

                <div style={styles.sidebarFooter}>
                    <button style={styles.logoutBtn} onClick={handleLogout}>
                        <Power size={18} />
                        <span>Sign Out</span>
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main style={styles.content}>
                {isOfflineMode && (
                    <div style={{
                        background: '#fffbeb',
                        border: '1.5px solid #fde68a',
                        borderRadius: '12px',
                        padding: '14px 20px',
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '12px'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '20px' }}>⚠️</span>
                            <div>
                                <strong style={{ color: '#92400e', fontSize: '13.5px', display: 'block' }}>
                                    Cloud Run Backend Currently Offline (503) &bull; Operational Control Panel Active
                                </strong>
                                <span style={{ color: '#b45309', fontSize: '12px' }}>
                                    Operating in resilient Administrative Mode. Full User Directory, 19 Council Members, Course Catalog & Metrics are loaded.
                                </span>
                            </div>
                        </div>
                        <button 
                            onClick={() => fetchData(true)}
                            style={{
                                background: '#d97706',
                                color: '#ffffff',
                                border: 'none',
                                padding: '6px 14px',
                                borderRadius: '8px',
                                fontSize: '12px',
                                fontWeight: '700',
                                cursor: 'pointer'
                            }}
                        >
                            🔄 Reconnect Backend
                        </button>
                    </div>
                )}
                <header style={styles.contentHeader}>
                    <div style={styles.headerTitleGroup}>
                        <h1 style={styles.headerTitle}>ARIVUNEST Dashboard</h1>
                        <p style={styles.headerMeta}>Real-time system health & management</p>
                    </div>
                    <div style={styles.headerActions}>
                        <div style={styles.pollIndicator}>
                            <div style={{...styles.pulse, backgroundColor: isPolling ? '#22c55e' : '#94a3b8'}}></div>
                            <span>{isPolling ? 'Live Updates Active' : 'Updates Paused'}</span>
                        </div>
                        <button style={styles.refreshBtn} onClick={() => fetchData(true)}>
                            <RefreshCcw size={16} />
                        </button>
                    </div>
                </header>

                {/* Top Stats Grid */}
                {stats && (
                    <div style={styles.statsGrid}>
                        <div style={styles.statCard}>
                            <div style={styles.statLabel}>Total Learners</div>
                            <div style={styles.statValue}>{stats.total_users}</div>
                            <div style={styles.statChange}>Active Students: {stats.students}</div>
                        </div>
                        <div style={styles.statCard}>
                            <div style={styles.statLabel}>Academy Assets</div>
                            <div style={styles.statValue}>{stats.total_courses}</div>
                            <div style={styles.statChange}>{stats.published_courses} Published</div>
                        </div>
                        <div style={styles.statCard}>
                            <div style={styles.statLabel}>Platform Load</div>
                            <div style={styles.statValue}>{stats.total_enrollments}</div>
                            <div style={styles.statChange}>Total Enrollments</div>
                        </div>
                        <div style={styles.statCard}>
                            <div style={styles.statLabel}>Live Sessions</div>
                            <div style={styles.statValue}>{stats.total_live_classes}</div>
                            <div style={styles.statChange}>Active/Scheduled</div>
                        </div>
                    </div>
                )}

                {/* Central Management Area */}
                <div style={styles.managementCard}>
                    {/* View Switching */}
                    {activeTab === 'users' && (
                        <>
                            <div style={styles.cardHeader}>
                                <h2>User Directory</h2>
                                <div style={styles.tableFilters}>
                                    <div style={styles.searchBox}>
                                        <Search size={16} />
                                        <input 
                                            type="text" 
                                            placeholder="Search name/email..." 
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                    <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                                        <option value="all">Every Role</option>
                                        <option value="student">Students</option>
                                        <option value="instructor">Instructors</option>
                                        <option value="admin">Admins</option>
                                    </select>
                                </div>
                            </div>
                            <div style={styles.tableWrapper}>
                                <table style={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Profile</th>
                                            <th>Contact</th>
                                            <th>Permission</th>
                                            <th>Status</th>
                                            <th>Registration</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredUsers.map(user => (
                                            <tr key={user.id}>
                                                <td>
                                                    <div style={styles.idCell}>
                                                        <strong>{user.full_name || 'Incognito'}</strong>
                                                        <span>{user.email}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div style={styles.idCell}>
                                                        {user.mobile_no ? (
                                                            <span style={styles.code}>{user.mobile_no}</span>
                                                        ) : (
                                                            <span style={{color: '#94a3b8', fontSize: '12px'}}>N/A</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <select 
                                                        value={user.role} 
                                                        onChange={(e) => handleRoleChange(user.id, e.target.value)}
                                                        style={{...styles.roleTag, color: getRoleColor(user.role)}}
                                                    >
                                                        <option value="student">Student</option>
                                                        <option value="instructor">Instructor</option>
                                                        <option value="admin">Admin</option>
                                                    </select>
                                                </td>
                                                <td>
                                                    <span style={user.is_active ? styles.statusActive : styles.statusInactive}>
                                                        {user.is_active ? 'Active' : 'Locked'}
                                                    </span>
                                                </td>
                                                <td>{new Date(user.created_at).toLocaleDateString()}</td>
                                                <td>
                                                    <div style={styles.actionRow}>
                                                        <button onClick={() => handleToggleActive(user.id)} title="Switch status">
                                                            {user.is_active ? <Pause size={14} /> : <Play size={14} />}
                                                        </button>
                                                        <button onClick={() => handleDeleteUser(user.id, user.email)} style={{color: '#ef4444'}}>
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                    {activeTab === 'referrals' && (
                        <>
                            <div style={styles.cardHeader}>
                                <div>
                                    <h2 style={{ margin: '0 0 6px', fontSize: '20px', color: '#0f172a' }}>
                                        🏆 2026 Advisory Committee Referral Points & Election Engine
                                    </h2>
                                    <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
                                        Credit referral points when new candidates pay the ₹100 membership fee on Google Form quoting a Reference ID.
                                    </p>
                                </div>
                                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                    <button
                                        onClick={() => {
                                            if (window.confirm('Reset all referral points to real Google Form submissions (Dr. T. Ezhilarasi = 20 pts, all others = 0)?')) {
                                                resetAllPointsToForm();
                                                setPointsState(getAllPoints());
                                                setActionMessage('Reset to real Google Form submissions!');
                                                setTimeout(() => setActionMessage(''), 3000);
                                            }
                                        }}
                                        style={{
                                            ...styles.actionBtn,
                                            background: '#fef2f2',
                                            color: '#991b1b',
                                            border: '1px solid #fecaca',
                                            fontWeight: '700'
                                        }}
                                    >
                                        🔄 Sync to Real Google Form
                                    </button>
                                    <a
                                        href="https://forms.gle/UfkUncKELYenUJE46"
                                        target="_blank"
                                        rel="noreferrer"
                                        style={styles.primaryAction}
                                    >
                                        Open Google Form (₹100)
                                    </a>
                                </div>
                            </div>

                            <div style={styles.tableWrapper}>
                                <table style={styles.table}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '60px', textAlign: 'center' }}>Rank</th>
                                            <th>Member Name & Ref ID</th>
                                            <th>Projected Leadership Post</th>
                                            <th>Institution</th>
                                            <th style={{ textAlign: 'center' }}>Current Points</th>
                                            <th>Award Points (New ₹100 Reg)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {getRankedMembers(VERIFIED_MEMBERS).map((m) => (
                                            <tr key={m.refId} style={{ background: m.rank === 1 ? '#f0fdf4' : m.rank <= 3 ? '#faf5ff' : '#ffffff' }}>
                                                <td style={{ textAlign: 'center', fontWeight: '800' }}>
                                                    {m.rank === 1 ? '👑' : m.rank === 2 ? '🥈' : m.rank === 3 ? '🥉' : `#${m.rank}`}
                                                </td>
                                                <td>
                                                    <div style={styles.idCell}>
                                                        <strong>{m.name}</strong>
                                                        <span style={{ color: '#059669', fontFamily: 'monospace' }}>{m.refId}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span style={{
                                                        background: m.roleDesignation.bg,
                                                        color: m.roleDesignation.color,
                                                        padding: '4px 8px',
                                                        borderRadius: '6px',
                                                        fontSize: '11.5px',
                                                        fontWeight: '700'
                                                    }}>
                                                        {m.roleDesignation.title}
                                                    </span>
                                                </td>
                                                <td style={{ fontSize: '12.5px', color: '#475569' }}>{m.institution}</td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <span style={{
                                                        background: '#ecfdf5',
                                                        color: '#047857',
                                                        padding: '4px 10px',
                                                        borderRadius: '999px',
                                                        fontWeight: '800',
                                                        fontSize: '13px'
                                                    }}>
                                                        ⭐ {m.referralPoints} pts
                                                    </span>
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', gap: '8px' }}>
                                                        <button
                                                            onClick={() => handleAddPoints(m.refId, m.name, 10)}
                                                            style={{
                                                                background: '#047857',
                                                                color: '#ffffff',
                                                                border: 'none',
                                                                padding: '6px 12px',
                                                                borderRadius: '6px',
                                                                fontSize: '12px',
                                                                fontWeight: '700',
                                                                cursor: 'pointer'
                                                            }}
                                                            title="Credit +10 Points for 1 new ₹100 registration"
                                                        >
                                                            +10 Pts (₹100)
                                                        </button>
                                                        <button
                                                            onClick={() => handleAddPoints(m.refId, m.name, 50)}
                                                            style={{
                                                                background: '#0369a1',
                                                                color: '#ffffff',
                                                                border: 'none',
                                                                padding: '6px 12px',
                                                                borderRadius: '6px',
                                                                fontSize: '12px',
                                                                fontWeight: '700',
                                                                cursor: 'pointer'
                                                            }}
                                                            title="Credit +50 Points for 5 new registrations"
                                                        >
                                                            +50 Pts
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                    {activeTab === 'courses' && (
                        <>
                            <div style={styles.cardHeader}>
                                <h2>Global Course Ledger</h2>
                                <button style={styles.primaryAction} onClick={() => navigate('/instructor-dashboard')}>
                                    Course Builder
                                </button>
                            </div>
                            <div style={styles.tableWrapper}>
                                <table style={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Course Detail</th>
                                            <th>Architect</th>
                                            <th>Tier</th>
                                            <th>Metrics</th>
                                            <th>Visibility</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {courses.map(course => (
                                            <tr key={course.id}>
                                                <td>
                                                    <div style={styles.idCell}>
                                                        <strong>{course.title}</strong>
                                                        <span>ID: {course.id}</span>
                                                    </div>
                                                </td>
                                                <td>{course.instructor_name}</td>
                                                <td>{course.category}</td>
                                                <td>{course.enrollment_count} Learners</td>
                                                <td>
                                                    <span style={course.is_published ? styles.statusActive : styles.statusInactive}>
                                                        {course.is_published ? 'Public' : 'Draft'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                    {activeTab === 'referrals' && (
                        <>
                            <div style={styles.cardHeader}>
                                <div>
                                    <h2 style={{ margin: '0 0 6px', fontSize: '20px', color: '#0f172a' }}>
                                        🏆 2026 Advisory Committee Referral Points & Election Engine
                                    </h2>
                                    <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
                                        Credit referral points when new candidates pay the ₹100 membership fee on Google Form quoting a Reference ID.
                                    </p>
                                </div>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <a
                                        href="https://forms.gle/UfkUncKELYenUJE46"
                                        target="_blank"
                                        rel="noreferrer"
                                        style={styles.primaryAction}
                                    >
                                        Open Google Form (₹100)
                                    </a>
                                </div>
                            </div>

                            <div style={styles.tableWrapper}>
                                <table style={styles.table}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '60px', textAlign: 'center' }}>Rank</th>
                                            <th>Member Name & Ref ID</th>
                                            <th>Projected Leadership Post</th>
                                            <th>Institution</th>
                                            <th style={{ textAlign: 'center' }}>Current Points</th>
                                            <th>Award Points (New ₹100 Reg)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {getRankedMembers(VERIFIED_MEMBERS).map((m) => (
                                            <tr key={m.refId} style={{ background: m.rank === 1 ? '#f0fdf4' : m.rank <= 3 ? '#faf5ff' : '#ffffff' }}>
                                                <td style={{ textAlign: 'center', fontWeight: '800' }}>
                                                    {m.rank === 1 ? '👑' : m.rank === 2 ? '🥈' : m.rank === 3 ? '🥉' : `#${m.rank}`}
                                                </td>
                                                <td>
                                                    <div style={styles.idCell}>
                                                        <strong>{m.name}</strong>
                                                        <span style={{ color: '#059669', fontFamily: 'monospace' }}>{m.refId}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span style={{
                                                        background: m.roleDesignation.bg,
                                                        color: m.roleDesignation.color,
                                                        padding: '4px 8px',
                                                        borderRadius: '6px',
                                                        fontSize: '11.5px',
                                                        fontWeight: '700'
                                                    }}>
                                                        {m.roleDesignation.title}
                                                    </span>
                                                </td>
                                                <td style={{ fontSize: '12.5px', color: '#475569' }}>{m.institution}</td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <span style={{
                                                        background: '#ecfdf5',
                                                        color: '#047857',
                                                        padding: '4px 10px',
                                                        borderRadius: '999px',
                                                        fontWeight: '800',
                                                        fontSize: '13px'
                                                    }}>
                                                        ⭐ {m.referralPoints} pts
                                                    </span>
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', gap: '8px' }}>
                                                        <button
                                                            onClick={() => handleAddPoints(m.refId, m.name, 10)}
                                                            style={{
                                                                background: '#047857',
                                                                color: '#ffffff',
                                                                border: 'none',
                                                                padding: '6px 12px',
                                                                borderRadius: '6px',
                                                                fontSize: '12px',
                                                                fontWeight: '700',
                                                                cursor: 'pointer'
                                                            }}
                                                            title="Credit +10 Points for 1 new ₹100 registration"
                                                        >
                                                            +10 Pts (₹100)
                                                        </button>
                                                        <button
                                                            onClick={() => handleAddPoints(m.refId, m.name, 50)}
                                                            style={{
                                                                background: '#0369a1',
                                                                color: '#ffffff',
                                                                border: 'none',
                                                                padding: '6px 12px',
                                                                borderRadius: '6px',
                                                                fontSize: '12px',
                                                                fontWeight: '700',
                                                                cursor: 'pointer'
                                                            }}
                                                            title="Credit +50 Points for 5 new registrations"
                                                        >
                                                            +50 Pts
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                    {activeTab === 'live' && (
                        <>
                            <div style={styles.cardHeader}>
                                <h2>Live Session Monitor</h2>
                            </div>
                            <div style={styles.tableWrapper}>
                                <table style={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Session Name</th>
                                            <th>Host</th>
                                            <th>Broadcast Time</th>
                                            <th>Current Phase</th>
                                            <th>Room Connect</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {liveClasses.map(cls => (
                                            <tr key={cls.id}>
                                                <td><strong>{cls.title}</strong></td>
                                                <td>{cls.instructor_name}</td>
                                                <td>{new Date(cls.scheduled_at).toLocaleString()}</td>
                                                <td>
                                                    <span style={{...styles.roleTag, color: cls.status === 'live' ? '#22c55e' : '#64748b'}}>
                                                        {cls.status.toUpperCase()}
                                                    </span>
                                                </td>
                                                <td>
                                                    <code style={styles.code}>{cls.meeting_id || 'N/A'}</code>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                    {activeTab === 'referrals' && (
                        <>
                            <div style={styles.cardHeader}>
                                <div>
                                    <h2 style={{ margin: '0 0 6px', fontSize: '20px', color: '#0f172a' }}>
                                        🏆 2026 Advisory Committee Referral Points & Election Engine
                                    </h2>
                                    <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
                                        Credit referral points when new candidates pay the ₹100 membership fee on Google Form quoting a Reference ID.
                                    </p>
                                </div>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <a
                                        href="https://forms.gle/UfkUncKELYenUJE46"
                                        target="_blank"
                                        rel="noreferrer"
                                        style={styles.primaryAction}
                                    >
                                        Open Google Form (₹100)
                                    </a>
                                </div>
                            </div>

                            <div style={styles.tableWrapper}>
                                <table style={styles.table}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '60px', textAlign: 'center' }}>Rank</th>
                                            <th>Member Name & Ref ID</th>
                                            <th>Projected Leadership Post</th>
                                            <th>Institution</th>
                                            <th style={{ textAlign: 'center' }}>Current Points</th>
                                            <th>Award Points (New ₹100 Reg)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {getRankedMembers(VERIFIED_MEMBERS).map((m) => (
                                            <tr key={m.refId} style={{ background: m.rank === 1 ? '#f0fdf4' : m.rank <= 3 ? '#faf5ff' : '#ffffff' }}>
                                                <td style={{ textAlign: 'center', fontWeight: '800' }}>
                                                    {m.rank === 1 ? '👑' : m.rank === 2 ? '🥈' : m.rank === 3 ? '🥉' : `#${m.rank}`}
                                                </td>
                                                <td>
                                                    <div style={styles.idCell}>
                                                        <strong>{m.name}</strong>
                                                        <span style={{ color: '#059669', fontFamily: 'monospace' }}>{m.refId}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span style={{
                                                        background: m.roleDesignation.bg,
                                                        color: m.roleDesignation.color,
                                                        padding: '4px 8px',
                                                        borderRadius: '6px',
                                                        fontSize: '11.5px',
                                                        fontWeight: '700'
                                                    }}>
                                                        {m.roleDesignation.title}
                                                    </span>
                                                </td>
                                                <td style={{ fontSize: '12.5px', color: '#475569' }}>{m.institution}</td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <span style={{
                                                        background: '#ecfdf5',
                                                        color: '#047857',
                                                        padding: '4px 10px',
                                                        borderRadius: '999px',
                                                        fontWeight: '800',
                                                        fontSize: '13px'
                                                    }}>
                                                        ⭐ {m.referralPoints} pts
                                                    </span>
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', gap: '8px' }}>
                                                        <button
                                                            onClick={() => handleAddPoints(m.refId, m.name, 10)}
                                                            style={{
                                                                background: '#047857',
                                                                color: '#ffffff',
                                                                border: 'none',
                                                                padding: '6px 12px',
                                                                borderRadius: '6px',
                                                                fontSize: '12px',
                                                                fontWeight: '700',
                                                                cursor: 'pointer'
                                                            }}
                                                            title="Credit +10 Points for 1 new ₹100 registration"
                                                        >
                                                            +10 Pts (₹100)
                                                        </button>
                                                        <button
                                                            onClick={() => handleAddPoints(m.refId, m.name, 50)}
                                                            style={{
                                                                background: '#0369a1',
                                                                color: '#ffffff',
                                                                border: 'none',
                                                                padding: '6px 12px',
                                                                borderRadius: '6px',
                                                                fontSize: '12px',
                                                                fontWeight: '700',
                                                                cursor: 'pointer'
                                                            }}
                                                            title="Credit +50 Points for 5 new registrations"
                                                        >
                                                            +50 Pts
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>

                {/* Toast Notification */}
                {actionMessage && (
                    <div style={styles.toast}>
                        <span>{actionMessage}</span>
                    </div>
                )}
            </main>
        </div>
    );
};

const getRoleColor = (role) => {
    switch(role) {
        case 'admin': return '#9333ea';
        case 'instructor': return '#2563eb';
        default: return '#0f172a';
    }
};

const styles = {
    container: {
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        fontFamily: 'Inter, sans-serif'
    },
    sidebar: {
        width: '280px',
        backgroundColor: '#0f172a',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        padding: '24px 16px',
        position: 'fixed',
        height: '100vh',
    },
    logoSlot: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '40px',
        padding: '0 8px'
    },
    logoIcon: {
        width: '32px',
        height: '32px',
        backgroundColor: '#22c55e',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 'bold',
        fontSize: '14px'
    },
    logoText: {
        fontSize: '18px',
        fontWeight: '700',
        letterSpacing: '-0.5px'
    },
    nav: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        flex: 1
    },
    navItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 16px',
        borderRadius: '10px',
        color: '#94a3b8',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500',
        transition: 'all 0.2s',
        textAlign: 'left'
    },
    navItemActive: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 16px',
        borderRadius: '10px',
        color: 'white',
        backgroundColor: 'rgba(255,255,255,0.1)',
        border: 'none',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600',
        textAlign: 'left'
    },
    sidebarFooter: {
        marginTop: 'auto',
        borderTop: '1px solid rgba(255,255,255,0.1)',
        paddingTop: '20px'
    },
    logoutBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        width: '100%',
        padding: '12px 16px',
        borderRadius: '10px',
        color: '#f87171',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500'
    },
    content: {
        marginLeft: '280px',
        flex: 1,
        padding: '40px'
    },
    contentHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '32px'
    },
    headerTitle: {
        fontSize: '28px',
        fontWeight: '800',
        color: '#0f172a',
        margin: 0
    },
    headerMeta: {
        color: '#64748b',
        fontSize: '14px',
        marginTop: '4px'
    },
    headerActions: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
    },
    pollIndicator: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '12px',
        color: '#64748b',
        backgroundColor: '#fff',
        padding: '8px 12px',
        borderRadius: '99px',
        border: '1px solid #e2e8f0'
    },
    pulse: {
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        boxShadow: '0 0 0 rgba(34, 197, 94, 0.4)',
    },
    refreshBtn: {
        padding: '8px',
        backgroundColor: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        cursor: 'pointer',
        color: '#64748b',
        transition: 'all 0.2s'
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '24px',
        marginBottom: '40px'
    },
    statCard: {
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
    },
    statLabel: {
        fontSize: '12px',
        fontWeight: '600',
        color: '#64748b',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    },
    statValue: {
        fontSize: '32px',
        fontWeight: '800',
        color: '#0f172a',
        margin: '8px 0'
    },
    statChange: {
        fontSize: '13px',
        color: '#22c55e',
        fontWeight: '500'
    },
    managementCard: {
        backgroundColor: 'white',
        borderRadius: '20px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
        overflow: 'hidden'
    },
    cardHeader: {
        padding: '24px',
        borderBottom: '1px solid #e2e8f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    tableFilters: {
        display: 'flex',
        gap: '12px'
    },
    searchBox: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        backgroundColor: '#f1f5f9',
        padding: '8px 16px',
        borderRadius: '10px',
        border: '1px solid #e2e8f0',
        width: '260px'
    },
    tableWrapper: {
        overflowX: 'auto'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        textAlign: 'left'
    },
    idCell: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px'
    },
    statusActive: {
        padding: '4px 8px',
        backgroundColor: '#dcfce7',
        color: '#15803d',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: '600'
    },
    statusInactive: {
        padding: '4px 8px',
        backgroundColor: '#fee2e2',
        color: '#b91c1c',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: '600'
    },
    roleTag: {
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '13px',
        fontWeight: '700',
        background: '#f1f5f9',
        border: 'none',
        outline: 'none',
        cursor: 'pointer'
    },
    actionRow: {
        display: 'flex',
        gap: '10px'
    },
    code: {
        fontFamily: 'monospace',
        backgroundColor: '#f1f5f9',
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px'
    },
    toast: {
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        backgroundColor: '#0f172a',
        color: 'white',
        padding: '16px 24px',
        borderRadius: '12px',
        boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        zIndex: 1000
    },
    spinner: {
        width: '40px',
        height: '40px',
        border: '3px solid #e2e8f0',
        borderTop: '3px solid #22c55e',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
    }
};

export default AdminDashboard;
